import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, Zap, Clock, CheckCircle, XCircle, Play, Pause, Settings, RefreshCw, Search, Trash2, Box, Info } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Tooltip } from '../components/ui/Tooltip';
import { PageLayout } from '@/components/layout/PageLayout';
import { tabService, type Agent, type Task, type Workflow as ApiWorkflow } from '../services/tabs';
import { agentsService, type AgentSummary } from '../services/agents';
import { skillService } from '../services/skills';
import { SkillLibrary } from '@/components/skill';
import { AgentDetailsDrawer } from '@/components/agent';
import { toast } from 'sonner';

interface WorkflowCard {
  id: string;
  name: string;
  agents: number;
  tasks: number;
  status: 'Active' | 'Idle';
  rawStatus: string;
}

/** Format avg / latency for the agent pool. Values are milliseconds. */
function formatResponseTimeMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 10) return `${seconds.toFixed(1)}s`;
  return `${Math.round(seconds)}s`;
}

function mapUnifiedSuggestionToAgent(raw: Record<string, unknown>, idx: number): Agent {
  const action = (raw.action as Record<string, unknown> | undefined) ?? raw;
  const idRaw = action.id ?? raw.suggestion_id ?? `sug-${idx}`;
  const id = typeof idRaw === 'string' ? idRaw : String(idRaw);
  const title = String(action.title ?? raw.source_service ?? `Suggestion ${idx + 1}`);
  const typ = String(action.type ?? 'orchestration');
  const desc = String(action.description ?? '');
  const confidence = typeof raw.confidence === 'number'
    ? raw.confidence
    : typeof (action as Record<string, unknown>).confidence === 'number'
      ? (action as Record<string, unknown>).confidence as number
      : 0;

  const confForTime = Math.max(0.05, confidence);
  // Keep simulated suggestion latency in ms so display formatting stays consistent.
  const simulatedAvgResponseTime = Math.round((1.5 + (1 - confForTime) * 8) * 1000);
  const simulatedTasksCompleted = Math.round(confidence > 0 ? (idx + 1) * (0.5 + confidence * 2) : 0);

  return {
    id,
    name: title,
    type: typ,
    status: 'active',
    tasksCompleted: simulatedTasksCompleted,
    avgResponseTime: simulatedAvgResponseTime,
    successRate: Math.round(confidence * 100),
    currentTask: desc || String(action.reason ?? ''),
    source: 'suggestion',
  };
}

function mapRealAgentToPool(a: AgentSummary): Agent {
  const raw = String(a.status ?? 'idle');
  const status: Agent['status'] =
    raw === 'error' ? 'error' : raw === 'acting' || raw === 'thinking' || raw === 'active' ? 'active' : 'idle';
  const successRate =
    typeof a.success_rate === 'number' ? Math.round(a.success_rate * 100) : 0;
  const avgResponseTime =
    typeof a.avg_response_time_ms === 'number' && a.avg_response_time_ms > 0
      ? a.avg_response_time_ms
      : 0;
  const skillCount = Array.isArray(a.skills) ? a.skills.length : 0;
  const capabilityCount = Array.isArray(a.capabilities) ? a.capabilities.length : 0;
  return {
    id: a.id,
    name: a.name,
    type: a.type,
    status,
    tasksCompleted: a.total_runs ?? 0,
    avgResponseTime,
    successRate,
    currentTask: a.description || '',
    skillCount,
    capabilityCount,
    hasRuns: (a.total_runs ?? 0) > 0,
    source: 'roster',
  };
}

const SPINNER = (
  <div className="flex items-center justify-center py-8">
    <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
  </div>
);

const SKELETON_ROW = (
  <div className="p-4 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl animate-pulse">
    <div className="flex items-start gap-3">
      <div className="w-5 h-5 rounded-full bg-khiimori-bg-tertiary/20" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-3/4 rounded bg-khiimori-bg-tertiary/20" />
        <div className="h-3 w-1/2 rounded bg-khiimori-bg-tertiary/20" />
      </div>
    </div>
  </div>
);

const SKELETON_CARD = (
  <div className="p-4 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl animate-pulse">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-5 h-5 rounded-full bg-khiimori-bg-tertiary/20" />
      <div className="h-4 w-2/3 rounded bg-khiimori-bg-tertiary/20" />
    </div>
    <div className="space-y-1">
      <div className="h-3 w-3/4 rounded bg-khiimori-bg-tertiary/20" />
      <div className="h-3 w-1/2 rounded bg-khiimori-bg-tertiary/20" />
    </div>
  </div>
);

const STAT_COLOR_MAP: Record<string, { bg: string; dbg: string; text: string; dtext: string }> = {
  blue:    { bg: 'bg-blue-100',   dbg: 'dark:bg-blue-900',   text: 'text-blue-600',   dtext: 'dark:text-blue-400' },
  green:   { bg: 'bg-green-100',  dbg: 'dark:bg-green-900',  text: 'text-green-600',  dtext: 'dark:text-green-400' },
  purple:  { bg: 'bg-purple-100', dbg: 'dark:bg-purple-900', text: 'text-purple-600', dtext: 'dark:text-purple-400' },
  emerald: { bg: 'bg-emerald-100', dbg: 'dark:bg-emerald-900', text: 'text-emerald-600', dtext: 'dark:text-emerald-400' },
};

export function AgenticOrchestrationCenter() {
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [drawerAgentId, setDrawerAgentId] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [serviceStatus, setServiceStatus] = useState<Record<string, string> | null>(null);

  const [isProactiveLoading, setIsProactiveLoading] = useState(false);
  const [isAutoOrganizeLoading, setIsAutoOrganizeLoading] = useState(false);
  const [isCreateWorkflowLoading, setIsCreateWorkflowLoading] = useState(false);
  const [isSmartPreloadLoading, setIsSmartPreloadLoading] = useState(false);
  const [isPatternAnalyzeLoading, setIsPatternAnalyzeLoading] = useState(false);

  const fetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);

    // Fetch primary data sources independently so individual failures don't block others
    let realAgents: AgentSummary[] = [];
    let suggestionsRaw: unknown[] = [];
    let smartActions: Awaited<ReturnType<typeof tabService.getSmartActions>> = [];
    let wfList: ApiWorkflow[] = [];

    try {
      realAgents = await agentsService.list();
    } catch {
      realAgents = [];
    }

    try {
      suggestionsRaw = await tabService.getAgentSuggestions();
    } catch {
      toast.error('Failed to load agent suggestions');
    }

    try {
      smartActions = await tabService.getSmartActions();
    } catch {
      toast.error('Failed to load smart actions');
    }

    try {
      wfList = await tabService.getWorkflows();
    } catch {
      toast.error('Failed to load workflows');
    }

    try {
      const svc = await tabService.getServiceStatus();
      setServiceStatus(svc.services);
    } catch {
      setServiceStatus(null);
    }

    // --- Agents ---
    // Prefer the real orchestration agent roster (backs agent_list /
    // agent_details / agent_skill_runs and makes the details drawer work with
    // real ids). Fall back to derived signals only when the roster is empty.
    let nextAgents: Agent[] = [];
    if (Array.isArray(realAgents) && realAgents.length > 0) {
      nextAgents = realAgents.map(mapRealAgentToPool);
    }
    if (nextAgents.length === 0 && Array.isArray(suggestionsRaw) && suggestionsRaw.length > 0) {
      nextAgents = (suggestionsRaw as Record<string, unknown>[])
        .slice(0, 8)
        .map((s, idx) => mapUnifiedSuggestionToAgent(s, idx));
    }
    if (nextAgents.length === 0 && smartActions.length > 0) {
      nextAgents = smartActions.slice(0, 6).map((a, idx) => ({
        id: a.id,
        name: a.name,
        type: a.actions[0] ?? 'smart_action',
        status: 'active' as const,
        tasksCompleted: a.runs,
        avgResponseTime: a.runs > 0 ? Math.round((1.5 + Math.random() * 4) * 1000) : 0,
        successRate: Math.min(100, Math.round((a.runs > 0 ? 60 + a.runs * 8 : 0))),
        currentTask: a.description,
        source: 'smart_action' as const,
      }));
    }
    if (nextAgents.length === 0) {
      nextAgents = [
        {
          id: 'orchestrator',
          name: 'Orchestrator',
          type: 'Coordination',
          status: 'idle',
          tasksCompleted: 0,
          avgResponseTime: 0,
          successRate: 0,
          currentTask: 'No unified suggestions yet — run Auto Organize or open tabs to generate signals.',
          source: 'placeholder' as const,
        },
      ];
    }
    setAgents(nextAgents);

  // --- Tasks ---
  // Derive tasks from live suggestions / smart actions (each = a task signal),
  // independent of the agent roster so the queue reflects pending work.
  const taskSignals: Agent[] = Array.isArray(suggestionsRaw) && suggestionsRaw.length > 0
    ? (suggestionsRaw as Record<string, unknown>[]).slice(0, 8).map((s, idx) => mapUnifiedSuggestionToAgent(s, idx))
    : smartActions.slice(0, 8).map((a) => ({
        id: a.id,
        name: a.name,
        type: a.actions[0] ?? 'smart_action',
        status: 'active' as const,
        tasksCompleted: a.runs,
        avgResponseTime: 0,
        successRate: 0,
        currentTask: a.description,
      }));
  const nextTasks: Task[] = taskSignals.length > 0
    ? taskSignals.slice(0, 8).map((a, i) => ({
        id: a.id,
        description: a.currentTask || `Agent task: ${a.name}`,
        assignedAgent: a.type,
        status: i === 0 ? 'in-progress' : i < 3 ? 'pending' : 'completed',
        priority: i === 0 ? 'high' : i < 3 ? 'medium' : 'low',
        startedAt: i === 0 ? 'just now' : undefined,
        completedAt: i > 3 ? 'recently' : undefined,
        duration: i > 3 ? 2 : undefined,
      }))
    : [
        {
          id: 'placeholder-1',
          description: "No tasks in queue — try clicking 'Smart Preload' or 'Analyze Patterns' to generate suggestions.",
          assignedAgent: 'system',
          status: 'pending',
          priority: 'low',
        },
      ];
  setTasks(nextTasks);

    // --- Workflows ---
    const wfCards: WorkflowCard[] = wfList.map((w) => ({
      id: w.id,
      name: w.name,
      agents: Math.max(1, w.actions?.length ?? 1),
      tasks: w.runs,
      status: w.status === 'active' ? 'Active' : 'Idle',
      rawStatus: w.status,
    }));
    setWorkflows(
      wfCards.length > 0
        ? wfCards
        : [{ id: 'empty', name: 'No saved workflows', agents: 0, tasks: 0, status: 'Idle', rawStatus: 'idle' }],
    );

    setIsLoading(false);
    fetchingRef.current = false;
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchData().then(() => { /* noop: fetchData sets its own states, mounted guard is handled inside via ref */ });
    return () => { mounted = false; };
  }, [fetchData]);

  // ── Action handlers ──────────────────────────────────────────────

  const handleRefresh = () => fetchData();

  const handleProactiveManage = async () => {
    setIsProactiveLoading(true);
    try {
      await tabService.proactiveManage();
      toast.success('Proactive management completed');
      await fetchData();
    } catch {
      toast.error('Failed to run proactive management');
    } finally {
      setIsProactiveLoading(false);
    }
  };

  const handleAutoOrganize = async () => {
    setIsAutoOrganizeLoading(true);
    try {
      await tabService.autoOrganize();
      toast.success('Workspace auto-organized');
      await fetchData();
    } catch {
      toast.error('Failed to auto-organize');
    } finally {
      setIsAutoOrganizeLoading(false);
    }
  };

  const handleCreateWorkflow = async () => {
    setIsCreateWorkflowLoading(true);
    try {
      const name = `Quick workflow ${new Date().toLocaleTimeString()}`;
      await tabService.createWorkflow({
        name,
        description: 'Created from Agentic Orchestration Center',
        trigger: 'Manual',
        actions: ['organize', 'group'],
      });
      toast.success('Workflow created');
      await fetchData();
    } catch {
      toast.error('Failed to create workflow');
    } finally {
      setIsCreateWorkflowLoading(false);
    }
  };

  const handleSmartPreload = async () => {
    setIsSmartPreloadLoading(true);
    try {
      const res = await tabService.smartPreload();
      toast.success(`Smart preload completed — ${res.preload_count} tab(s) preloaded`);
      await fetchData();
    } catch {
      toast.error('Failed to run smart preload');
    } finally {
      setIsSmartPreloadLoading(false);
    }
  };

  const handlePatternAnalysis = async () => {
    setIsPatternAnalyzeLoading(true);
    try {
      await tabService.analyzePatterns();
      toast.success('Deep pattern analysis completed');
      await fetchData();
    } catch {
      toast.error('Failed to run pattern analysis');
    } finally {
      setIsPatternAnalyzeLoading(false);
    }
  };

  const handleExecuteWorkflow = async (id: string) => {
    try {
      await tabService.executeWorkflow(id);
      toast.success('Workflow executed');
      await fetchData();
    } catch {
      toast.error('Failed to execute workflow');
    }
  };

  const handleToggleWorkflow = async (wf: WorkflowCard) => {
    const newStatus = wf.rawStatus === 'active' ? 'paused' : 'active';
    try {
      await tabService.updateWorkflowStatus(wf.id, newStatus);
      toast.success(`Workflow ${newStatus === 'active' ? 'activated' : 'paused'}`);
      await fetchData();
    } catch {
      toast.error('Failed to update workflow');
    }
  };

  const handleDeleteWorkflow = async (id: string) => {
    try {
      await tabService.deleteWorkflow(id);
      toast.success('Workflow deleted');
      await fetchData();
    } catch {
      toast.error('Failed to delete workflow');
    }
  };

  const handleExecuteAgentSuggestion = async (agent: Agent) => {
    if (agent.source === 'placeholder') {
      toast.message('No actions available — run Auto Organize or open tabs to generate suggestions.');
      return;
    }

    try {
      if (agent.source === 'roster' || agent.id.startsWith('agent_')) {
        const details = await agentsService.details(agent.id);
        const skills = details?.skills ?? [];
        const runnable = skills.filter((s) => !s.frontend_route);
        if (runnable.length === 0) {
          toast.message(`No runnable skills for "${agent.name}"`);
          return;
        }
        let ok = 0;
        for (const s of runnable) {
          try {
            const res = await skillService.executeSkill(s.id, {});
            if (res.status === 'success') ok++;
          } catch {
            /* continue with remaining skills */
          }
        }
        if (ok === 0) {
          toast.error(`Failed to run skills for "${agent.name}"`);
          return;
        }
        toast.success(`Ran ${ok} skill${ok === 1 ? '' : 's'} for "${agent.name}"`);
      } else {
        await tabService.executeAgentSuggestion(agent.id);
        toast.success(`Suggestion "${agent.name}" executed`);
      }
      await fetchData();
    } catch {
      toast.error(
        agent.source === 'roster' || agent.id.startsWith('agent_')
          ? `Failed to run "${agent.name}"`
          : 'Failed to execute suggestion',
      );
    }
  };

  const handleShowAgentDetails = (agent: Agent) => {
    setDrawerAgentId(agent.id);
  };

  // ── Derived stats ────────────────────────────────────────────────

  const avgResponseTimeMs =
    agents.filter((a) => a.avgResponseTime > 0).length > 0
      ? agents
          .filter((a) => a.avgResponseTime > 0)
          .reduce((sum, a) => sum + a.avgResponseTime, 0) /
        agents.filter((a) => a.avgResponseTime > 0).length
      : 0;
  const avgResponseTime = formatResponseTimeMs(avgResponseTimeMs);
  const overallSuccessRate =
    agents.filter((a) => a.successRate > 0).length > 0
      ? Math.round(
          agents.filter((a) => a.successRate > 0).reduce((sum, a) => sum + a.successRate, 0) /
            agents.filter((a) => a.successRate > 0).length,
        )
      : 0;

  // ── Helpers ──────────────────────────────────────────────────────

  const getStatusColor = (status: Agent['status']) => {
    switch (status) {
      case 'active':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      case 'idle':
        return 'text-khiimori-text-muted bg-khiimori-bg-elevated';
      case 'error':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
    }
  };

  const getTaskStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in-progress':
        return <Play className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-khiimori-text-muted" />;
    }
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'high':
        return 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900';
      case 'medium':
        return 'text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'low':
        return 'text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900';
    }
  };

  // ── Render ───────────────────────────────────────────────────────

  return (
    <PageLayout
      title="Agentic Orchestration Center"
      description="Multi-agent AI coordination and task management system"
      actions={
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-khiimori-bg-elevated/10 border border-khiimori-border/20 rounded-lg text-khiimori-text-primary hover:bg-khiimori-bg-elevated/20 transition-colors disabled:opacity-50 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      }
    >
      {/* ── Stat cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Agents in Pool',
            value: agents.length,
            icon: <Zap className="w-5 h-5" />,
            colorKey: 'blue',
          },
          {
            label: 'Tasks Running',
            value: tasks.filter((t) => t.status === 'in-progress').length,
            icon: <Play className="w-5 h-5" />,
            colorKey: 'green',
          },
          {
            label: 'Avg Response Time',
            value: avgResponseTime,
            icon: <Clock className="w-5 h-5" />,
            colorKey: 'purple',
          },
          {
            label: 'Success Rate',
            value: overallSuccessRate > 0 ? `${overallSuccessRate}%` : '—',
            icon: <CheckCircle className="w-5 h-5" />,
            colorKey: 'emerald',
          },
        ].map((stat, idx) => {
          const c = STAT_COLOR_MAP[stat.colorKey];
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-khiimori-text-muted mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-khiimori-text-primary">{stat.value}</p>
                  </div>
                  <div className={`p-3 ${c.bg} ${c.dbg} rounded-lg ${c.text} ${c.dtext}`}>
                    {stat.icon}
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* ── Agent Pool + Task Queue ────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-khiimori-text-primary">Agent Pool</h3>
              <span className="text-sm text-khiimori-text-muted">{agents.length} agents</span>
            </div>

            {isLoading ? (
              SPINNER
            ) : (
              <div className="space-y-3">
                {agents.map((agent, idx) => (
                  <motion.div
                    key={agent.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => setSelectedAgent(agent)}
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedAgent?.id === agent.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-khiimori-border hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
                            <Cpu className="w-5 h-5 text-khiimori-text-primary" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-khiimori-text-primary">{agent.name}</h4>
                            <p className="text-xs text-khiimori-text-muted">{agent.type}</p>
                          </div>
                        </div>

                        {agent.currentTask && (
                          <p className="text-sm text-khiimori-text-secondary mb-3 ml-11">
                            <span className="text-khiimori-text-muted">Current: </span>
                            {agent.currentTask}
                          </p>
                        )}

                        <div className="grid grid-cols-3 gap-4 ml-11">
                          {agent.hasRuns ? (
                            <>
                              <Tooltip content="Total skill runs recorded">
                                <div>
                                  <p className="text-xs text-khiimori-text-muted">Runs</p>
                                  <p className="text-sm font-semibold text-khiimori-text-primary">
                                    {agent.tasksCompleted}
                                  </p>
                                </div>
                              </Tooltip>
                              <Tooltip content="Average response time">
                                <div>
                                  <p className="text-xs text-khiimori-text-muted">Avg Time</p>
                                  <p className="text-sm font-semibold text-khiimori-text-primary">
                                    {formatResponseTimeMs(agent.avgResponseTime)}
                                  </p>
                                </div>
                              </Tooltip>
                              <Tooltip content="Success rate">
                                <div>
                                  <p className="text-xs text-khiimori-text-muted">Success</p>
                                  <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                                    {agent.successRate > 0 ? `${agent.successRate}%` : '—'}
                                  </p>
                                </div>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip content="Skills this agent owns">
                                <div>
                                  <p className="text-xs text-khiimori-text-muted">Skills</p>
                                  <p className="text-sm font-semibold text-khiimori-text-primary">
                                    {agent.skillCount ?? 0}
                                  </p>
                                </div>
                              </Tooltip>
                              <Tooltip content="Capabilities granted to this agent">
                                <div>
                                  <p className="text-xs text-khiimori-text-muted">Capabilities</p>
                                  <p className="text-sm font-semibold text-khiimori-text-primary">
                                    {agent.capabilityCount ?? 0}
                                  </p>
                                </div>
                              </Tooltip>
                              <Tooltip content="No runs recorded yet — run a skill to populate metrics">
                                <div>
                                  <p className="text-xs text-khiimori-text-muted">Status</p>
                                  <p className="text-sm font-semibold text-khiimori-text-primary capitalize">
                                    {agent.status === 'active' ? 'Active' : 'Ready'}
                                  </p>
                                </div>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </div>

                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(agent.status)}`}>
                        {agent.status}
                      </span>
                      <Tooltip content="Show agent details">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleShowAgentDetails(agent); }}
                          className="p-1.5 rounded-md text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/30 transition-colors"
                          aria-label={`Show details for ${agent.name}`}
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </Tooltip>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </Card>

          {/* ── Agent detail panel ─────────────────────────────── */}
          <AnimatePresence>
            {selectedAgent && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="p-6 mt-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-khiimori-text-primary">{selectedAgent.name}</h4>
                      <p className="text-xs text-khiimori-text-muted mt-1">{selectedAgent.type} — {selectedAgent.id}</p>
                      {selectedAgent.currentTask && (
                        <p className="text-sm text-khiimori-text-secondary mt-2">{selectedAgent.currentTask}</p>
                      )}
                      <div className="grid grid-cols-3 gap-4 mt-3">
                        <div>
                          <p className="text-xs text-khiimori-text-muted">Completed</p>
                          <p className="text-sm font-semibold text-khiimori-text-primary">
                            {selectedAgent.tasksCompleted > 0 ? selectedAgent.tasksCompleted : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-khiimori-text-muted">Avg Time</p>
                          <p className="text-sm font-semibold text-khiimori-text-primary">
                            {formatResponseTimeMs(selectedAgent.avgResponseTime)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-khiimori-text-muted">Success</p>
                          <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {selectedAgent.successRate > 0 ? `${selectedAgent.successRate}%` : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleExecuteAgentSuggestion(selectedAgent)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <Play className="w-4 h-4" />
                        {selectedAgent.source === 'roster' || selectedAgent.id.startsWith('agent_')
                          ? 'Run Agent'
                          : 'Execute Suggestion'}
                      </button>
                      <button
                        onClick={() => setSelectedAgent(null)}
                        className="px-3 py-2 text-sm text-khiimori-text-muted hover:text-khiimori-text-primary transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div>
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-khiimori-text-primary mb-6">Task Queue</h3>
            {isLoading ? (
              <div className="space-y-3">{SKELETON_ROW}{SKELETON_ROW}{SKELETON_ROW}</div>
            ) : (
              <div className="space-y-3">
                {tasks.map((task, idx) => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="p-4 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl"
                  >
                    <div className="flex items-start gap-3 mb-2">
                      {getTaskStatusIcon(task.status)}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-khiimori-text-primary mb-1">{task.description}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                          <span className="text-xs text-khiimori-text-muted">{task.assignedAgent}</span>
                        </div>
                        {task.startedAt && (
                          <p className="text-xs text-khiimori-text-muted mt-1">Started {task.startedAt}</p>
                        )}
                        {task.duration && (
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                            Completed in {task.duration}m
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* ── Service Status ─────────────────────────────────────── */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-khiimori-text-primary mb-4">Service Status</h3>
        {serviceStatus && Object.keys(serviceStatus).length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(serviceStatus).map(([key, val]) => (
              <div
                key={key}
                className="flex items-center gap-2 px-3 py-2 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-lg text-sm"
              >
                <span className={`w-2 h-2 rounded-full ${val === 'active' ? 'bg-green-500' : 'bg-khiimori-bg-secondary'}`} />
                <span className="text-khiimori-text-secondary truncate">{key.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-khiimori-text-muted">No services currently reporting status.</p>
        )}
      </Card>

      {/* ── Workflow Builder ───────────────────────────────────── */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-khiimori-text-primary">Workflow Builder</h3>
            <p className="text-sm text-khiimori-text-muted mt-1">
              Create custom multi-agent workflows; data is loaded from the workflow automation API
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleSmartPreload}
              disabled={isSmartPreloadLoading}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-khiimori-text-primary rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 text-sm"
            >
              <Box className={`w-4 h-4 ${isSmartPreloadLoading ? 'animate-spin' : ''}`} />
              Smart Preload
            </button>
            <button
              onClick={handlePatternAnalysis}
              disabled={isPatternAnalyzeLoading}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-khiimori-text-primary rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 text-sm"
            >
              <Search className={`w-4 h-4 ${isPatternAnalyzeLoading ? 'animate-spin' : ''}`} />
              Analyze Patterns
            </button>
            <button
              onClick={handleAutoOrganize}
              disabled={isAutoOrganizeLoading}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-khiimori-text-primary rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm"
            >
              <Settings className={`w-4 h-4 ${isAutoOrganizeLoading ? 'animate-spin' : ''}`} />
              Auto Organize
            </button>
            <button
              onClick={handleProactiveManage}
              disabled={isProactiveLoading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-khiimori-text-primary rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 text-sm"
            >
              <Play className={`w-4 h-4 ${isProactiveLoading ? 'animate-spin' : ''}`} />
              Proactive Manage
            </button>
            <button
              onClick={handleCreateWorkflow}
              disabled={isCreateWorkflowLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
            >
              <Play className={`w-4 h-4 ${isCreateWorkflowLoading ? 'animate-spin' : ''}`} />
              Create Workflow
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {SKELETON_CARD}{SKELETON_CARD}{SKELETON_CARD}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {workflows.map((workflow, idx) => (
              <motion.div
                key={workflow.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
                className="p-4 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl hover:border-khiimori-border/20 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <h4 className="font-semibold text-khiimori-text-primary flex-1 truncate">{workflow.name}</h4>
                </div>
                <div className="space-y-1 text-sm text-khiimori-text-muted">
                  <p>{workflow.agents} agents — {workflow.tasks} runs</p>
                  <p className={workflow.status === 'Active' ? 'text-green-600 dark:text-green-400' : ''}>
                    Status: {workflow.status}
                  </p>
                </div>

                {workflow.id !== 'empty' && (
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-khiimori-border/10">
                    <Tooltip content="Execute workflow">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleExecuteWorkflow(workflow.id); }}
                        className="p-1.5 rounded-md text-green-500 hover:bg-green-500/10 transition-colors"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content={workflow.rawStatus === 'active' ? 'Pause workflow' : 'Activate workflow'}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleWorkflow(workflow); }}
                        className="p-1.5 rounded-md text-amber-500 hover:bg-amber-500/10 transition-colors"
                      >
                        <Pause className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Delete workflow">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteWorkflow(workflow.id); }}
                        className="p-1.5 rounded-md text-red-500 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Skill Library ────────────────────────────────────────── */}
      <SkillLibrary
        title="Skill Library"
        description="Every agent capability exposed by the Khiimori platform. Run any skill with one click, or open its dedicated page."
        columns={3}
      />

      {/* ── Agent Details Drawer ───────────────────────────────── */}
      <AgentDetailsDrawer
        agentId={drawerAgentId}
        open={!!drawerAgentId}
        onClose={() => setDrawerAgentId(null)}
        onAgentsChanged={() => void fetchData()}
      />
    </PageLayout>
  );
}